// --- Types ---
export type ScopeLevel = 'global' | 'region' | 'community'
export type Scope = {
  level: ScopeLevel
  regionId?: string
  communityId?: string
  name: string
}

export type OptionLevel = 'standard' | 'level-1' | 'level-2' | 'level-3' | null

export const levelConfig: Record<
  string,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  standard: {
    label: 'Standard',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  'level-1': {
    label: 'Level 1',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  'level-2': {
    label: 'Level 2',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  'level-3': {
    label: 'Level 3',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
}

export type GlobalOption = {
  id: string
  name: string
  category: string
  subcategory: string
  variantSet: string
  basePrice: number
  description: string
  enabled: boolean
  isStandard: boolean
  hasImage: boolean
  imageUrl?: string
  tags?: string[]
}

export type OptionOverride = {
  optionId: string
  level: 'region' | 'community'
  levelId: string
  priceOverride?: number
  enabledOverride?: boolean
  nameOverride?: string
  descriptionOverride?: string
  isStandardOverride?: boolean
}

export type ResolvedOption = GlobalOption & {
  effectivePrice: number
  effectiveEnabled: boolean
  effectiveName: string
  effectiveDescription: string
  effectiveIsStandard: boolean
  overrideSource: 'global' | 'region' | 'community'
  hasOverride: boolean
  parentValue?: {
    price: number
    enabled: boolean
    name: string
    description: string
    isStandard: boolean
  }
}

export type NavigationLevel =
  | 'category'
  | 'subcategory'
  | 'variantSet'
  | 'options'
export type BreadcrumbItem = {
  label: string
  level: NavigationLevel
  value: string
}

// --- Catalogs (first-class entities) ---
export type Catalog = {
  id: string
  name: string
  description: string
  color: string
  subcategories: string[]
}

export const initialCatalogs: Catalog[] = [
  {
    id: 'catalog-exterior',
    name: 'Exterior',
    description: 'Roofing, siding, windows, and other exterior finishes',
    color: 'blue',
    subcategories: ['Roofing'],
  },
  {
    id: 'catalog-interior',
    name: 'Interior',
    description: 'Kitchen, flooring, fixtures, and interior finishes',
    color: 'amber',
    subcategories: ['Kitchen', 'Flooring'],
  },
]

// --- Mock Data ---
export const regions = [
  { id: 'alberta', name: 'Alberta Region' },
  { id: 'ontario', name: 'Ontario Region' },
  { id: 'bc', name: 'BC Region' },
  { id: 'texas', name: 'Texas Region' },
  { id: 'florida', name: 'Florida Region' },
  { id: 'arizona', name: 'Arizona Region' },
]

export const communities: Record<string, { id: string; name: string }[]> = {
  alberta: [
    { id: 'oakwood', name: 'Oakwood Estates' },
    { id: 'maple', name: 'Maple Ridge' },
    { id: 'pine', name: 'Pine Valley' },
    { id: 'cedar', name: 'Cedar Heights' },
  ],
  ontario: [
    { id: 'lakeside', name: 'Lakeside Living' },
    { id: 'urban', name: 'Urban Heights' },
    { id: 'riverdale', name: 'Riverdale Commons' },
  ],
  bc: [
    { id: 'mountain', name: 'Mountain View' },
    { id: 'pacific', name: 'Pacific Point' },
    { id: 'coastal', name: 'Coastal Breeze' },
  ],
  texas: [
    { id: 'austin-hills', name: 'Austin Hills' },
    { id: 'dallas-park', name: 'Dallas Park' },
    { id: 'houston-meadows', name: 'Houston Meadows' },
  ],
  florida: [
    { id: 'miami-shores', name: 'Miami Shores' },
    { id: 'tampa-bay', name: 'Tampa Bay Estates' },
    { id: 'orlando-gardens', name: 'Orlando Gardens' },
  ],
  arizona: [
    { id: 'phoenix-ridge', name: 'Phoenix Ridge' },
    { id: 'scottsdale-heights', name: 'Scottsdale Heights' },
  ],
}

export const initialGlobalOptions: GlobalOption[] = [
  {
    id: 'ext-1',
    name: 'Black Asphalt Shingles',
    category: 'Exterior',
    subcategory: 'Roofing',
    variantSet: 'Shingles',
    enabled: true,
    isStandard: true,
    basePrice: 0,
    description: 'Classic black asphalt shingles with 30-year warranty',
    hasImage: true,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/gy4nLAmMDnxDZrtDgT1xUc/black-asphalt.jpg',
    tags: ['Asphalt', '30-year warranty'],
  },
  {
    id: 'ext-2',
    name: 'Gray Asphalt Shingles',
    category: 'Exterior',
    subcategory: 'Roofing',
    variantSet: 'Shingles',
    enabled: true,
    isStandard: false,
    basePrice: 1200,
    description: 'Modern gray asphalt shingles - upgrade option',
    hasImage: true,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/7fXbSwM5gVoriK8Hvp5r78/gray-asphalt.jpg',
    tags: ['Asphalt', 'Modern'],
  },
  {
    id: 'int-1',
    name: 'Stainless Steel Appliances',
    category: 'Interior',
    subcategory: 'Kitchen',
    variantSet: 'Appliance Package',
    enabled: true,
    isStandard: false,
    basePrice: 2500,
    description: 'Premium stainless steel appliance package',
    hasImage: true,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
    tags: ['Appliances', 'Stainless'],
  },
  {
    id: 'int-2',
    name: 'Black + White Appliances',
    category: 'Interior',
    subcategory: 'Kitchen',
    variantSet: 'Appliance Package',
    enabled: true,
    isStandard: true,
    basePrice: 0,
    description: 'Classic black and white appliance package - included',
    hasImage: false,
    tags: ['Appliances', 'Basic'],
  },
  {
    id: 'int-3',
    name: 'Built-in Appliances',
    category: 'Interior',
    subcategory: 'Kitchen',
    variantSet: 'Appliance Package',
    enabled: true,
    isStandard: false,
    basePrice: 8500,
    description: 'Premium built-in appliance package with panel-ready fronts',
    hasImage: false,
    tags: ['Appliances', 'Built-in', 'Premium'],
  },
  {
    id: 'int-6',
    name: 'White Quartz Countertops',
    category: 'Interior',
    subcategory: 'Kitchen',
    variantSet: 'Countertops',
    enabled: true,
    isStandard: false,
    basePrice: 4500,
    description: 'Classic white quartz countertops',
    hasImage: false,
    tags: ['Quartz', 'Classic'],
  },
  {
    id: 'int-7',
    name: 'Laminate Countertops',
    category: 'Interior',
    subcategory: 'Kitchen',
    variantSet: 'Countertops',
    enabled: true,
    isStandard: true,
    basePrice: 0,
    description: 'Standard laminate countertops - included',
    hasImage: false,
    tags: ['Laminate', 'Standard'],
  },
  {
    id: 'int-8',
    name: 'Natural Oak Hardwood',
    category: 'Interior',
    subcategory: 'Flooring',
    variantSet: 'Hardwood',
    enabled: true,
    isStandard: false,
    basePrice: 8500,
    description: 'Classic natural oak hardwood flooring',
    hasImage: false,
    tags: ['Hardwood', 'Oak', 'Natural'],
  },
]

export const initialOverrides: OptionOverride[] = [
  {
    optionId: 'int-8',
    level: 'region',
    levelId: 'alberta',
    priceOverride: 7200,
  },
  {
    optionId: 'int-1',
    level: 'community',
    levelId: 'oakwood',
    isStandardOverride: true,
    priceOverride: 0,
  },
  {
    optionId: 'int-3',
    level: 'community',
    levelId: 'cedar',
    isStandardOverride: true,
    priceOverride: 0,
  },
  {
    optionId: 'int-2',
    level: 'community',
    levelId: 'cedar',
    isStandardOverride: false,
    priceOverride: 0,
  },
  {
    optionId: 'int-6',
    level: 'region',
    levelId: 'ontario',
    priceOverride: 5200,
  },
  {
    optionId: 'ext-2',
    level: 'community',
    levelId: 'maple',
    enabledOverride: false,
  },
]

export const initialLevelAssignments: Record<string, OptionLevel> = {
  'ext-1': 'standard',
  'ext-2': 'level-1',
  'int-1': 'level-1',
  'int-2': 'standard',
  'int-3': 'level-2',
  'int-6': 'level-2',
  'int-7': 'standard',
  'int-8': 'level-2',
}

// --- Models (separate from options) ---
export type ModelRoom = {
  id: string
  name: string
  sqft: number
  type: string
  floor: string
}

export type HouseModel = {
  id: string
  name: string
  sqft: number
  beds: number
  baths: number
  stories: number
  type: string
  basePrice: number
  description: string
  location: string
  status: 'active' | 'draft' | 'archived'
  hasImage: boolean
  imageUrl?: string
  communities: string[]
  assignedCatalogs: string[] // category IDs from the options catalog
  tags: string[]
  enabled?: boolean
  displayProductPrices?: boolean
  ueAssetPath?: string
  ueLevelName?: string
  rooms?: ModelRoom[]
}

export const initialModels: HouseModel[] = [
  {
    id: 'model-1',
    name: 'The Lawson',
    sqft: 1795,
    beds: 3,
    baths: 2.5,
    stories: 2,
    type: 'Laned Home',
    basePrice: 600000,
    description:
      'Two storey laned home with open-concept main floor and spacious bedrooms',
    location: 'Edmonton',
    status: 'active',
    hasImage: true,
    imageUrl:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    communities: ['oakwood', 'maple', 'pine'],
    assignedCatalogs: ['Exterior', 'Interior'],
    tags: ['Calgary', 'Two Storey', 'Laned'],
  },
  {
    id: 'model-2',
    name: 'The Ridgewood',
    sqft: 2450,
    beds: 4,
    baths: 3.5,
    stories: 2,
    type: 'Front Garage',
    basePrice: 785000,
    description:
      'Spacious front-attached garage home with bonus room and walk-in pantry',
    location: 'Edmonton',
    status: 'active',
    hasImage: true,
    imageUrl:
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=400&q=80',
    communities: ['oakwood', 'cedar', 'lakeside'],
    assignedCatalogs: ['Exterior', 'Interior'],
    tags: ['Front Garage', 'Bonus Room'],
  },
  {
    id: 'model-3',
    name: 'The Birchwood',
    sqft: 1450,
    beds: 3,
    baths: 2,
    stories: 1,
    type: 'Bungalow',
    basePrice: 520000,
    description:
      'Single-storey bungalow with main-floor master and covered patio',
    location: 'Calgary',
    status: 'active',
    hasImage: true,
    imageUrl:
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=400&q=80',
    communities: ['pine', 'maple'],
    assignedCatalogs: ['Exterior'],
    tags: ['Bungalow', 'Main Floor Master'],
  },
  {
    id: 'model-4',
    name: 'The Summit',
    sqft: 3200,
    beds: 5,
    baths: 4,
    stories: 2,
    type: 'Estate',
    basePrice: 1100000,
    description:
      'Luxury estate home with triple garage, theatre room, and wine cellar',
    location: 'Edmonton',
    status: 'draft',
    hasImage: false,
    communities: ['cedar'],
    assignedCatalogs: [],
    tags: ['Estate', 'Luxury', 'Triple Garage'],
  },
]

// --- Helper Functions ---
export const resolveOption = (
  globalOption: GlobalOption,
  scope: Scope,
  overrides: OptionOverride[],
): ResolvedOption => {
  let effectivePrice = globalOption.basePrice
  let effectiveEnabled = globalOption.enabled
  let effectiveName = globalOption.name
  let effectiveDescription = globalOption.description
  let effectiveIsStandard = globalOption.isStandard
  let overrideSource: 'global' | 'region' | 'community' = 'global'
  let hasOverride = false
  let parentValue = {
    price: globalOption.basePrice,
    enabled: globalOption.enabled,
    name: globalOption.name,
    description: globalOption.description,
    isStandard: globalOption.isStandard,
  }

  if (scope.level !== 'global' && scope.regionId) {
    const regionOverride = overrides.find(
      (o) =>
        o.optionId === globalOption.id &&
        o.level === 'region' &&
        o.levelId === scope.regionId,
    )
    if (regionOverride) {
      if (regionOverride.priceOverride !== undefined)
        effectivePrice = regionOverride.priceOverride
      if (regionOverride.enabledOverride !== undefined)
        effectiveEnabled = regionOverride.enabledOverride
      if (regionOverride.nameOverride !== undefined)
        effectiveName = regionOverride.nameOverride
      if (regionOverride.descriptionOverride !== undefined)
        effectiveDescription = regionOverride.descriptionOverride
      if (regionOverride.isStandardOverride !== undefined)
        effectiveIsStandard = regionOverride.isStandardOverride
      if (scope.level === 'region') {
        overrideSource = 'region'
        hasOverride = true
      } else {
        overrideSource = 'region'
        parentValue = {
          price: effectivePrice,
          enabled: effectiveEnabled,
          name: effectiveName,
          description: effectiveDescription,
          isStandard: effectiveIsStandard,
        }
      }
    }
  }

  if (scope.level === 'community' && scope.communityId) {
    const communityOverride = overrides.find(
      (o) =>
        o.optionId === globalOption.id &&
        o.level === 'community' &&
        o.levelId === scope.communityId,
    )
    if (communityOverride) {
      if (communityOverride.priceOverride !== undefined)
        effectivePrice = communityOverride.priceOverride
      if (communityOverride.enabledOverride !== undefined)
        effectiveEnabled = communityOverride.enabledOverride
      if (communityOverride.nameOverride !== undefined)
        effectiveName = communityOverride.nameOverride
      if (communityOverride.descriptionOverride !== undefined)
        effectiveDescription = communityOverride.descriptionOverride
      if (communityOverride.isStandardOverride !== undefined)
        effectiveIsStandard = communityOverride.isStandardOverride
      overrideSource = 'community'
      hasOverride = true
    }
  }

  return {
    ...globalOption,
    effectivePrice,
    effectiveEnabled,
    effectiveName,
    effectiveDescription,
    effectiveIsStandard,
    overrideSource,
    hasOverride,
    parentValue: hasOverride ? parentValue : undefined,
  }
}

// --- Additional community type and data to match updated snippet ---
export type BuilderCommunity = {
  id: string
  name: string
  builderName: string
  regionId: string
  regionName: string
  status: 'active' | 'draft' | 'archived'
  modelCount: number
  buyerCount: number
  lastActivity: string
  image: string
  description: string
}

export const builderCommunities: BuilderCommunity[] = [
  {
    id: 'oakwood',
    name: 'Oakwood Estates',
    builderName: 'Mattamy Homes',
    regionId: 'alberta',
    regionName: 'Alberta Region',
    status: 'active',
    modelCount: 4,
    buyerCount: 124,
    lastActivity: '2 mins ago',
    image:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80',
    description:
      'Single family homes collection featuring modern amenities and sustainable design.',
  },
  {
    id: 'maple',
    name: 'Maple Ridge',
    builderName: 'Schell Brothers',
    regionId: 'alberta',
    regionName: 'Alberta Region',
    status: 'active',
    modelCount: 3,
    buyerCount: 89,
    lastActivity: '1 hour ago',
    image:
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80',
    description: 'Premium townhomes with mountain views and modern finishes.',
  },
  {
    id: 'lakeside',
    name: 'Lakeside Living',
    builderName: 'Mattamy Homes',
    regionId: 'ontario',
    regionName: 'Ontario Region',
    status: 'active',
    modelCount: 5,
    buyerCount: 203,
    lastActivity: '30 mins ago',
    image:
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=600&q=80',
    description: 'Waterfront properties with luxury finishes and lake access.',
  },
  {
    id: 'urban',
    name: 'Urban Heights',
    builderName: 'Excel Homes',
    regionId: 'ontario',
    regionName: 'Ontario Region',
    status: 'draft',
    modelCount: 2,
    buyerCount: 0,
    lastActivity: '2 days ago',
    image:
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=600&q=80',
    description: 'Modern urban condos in the heart of downtown.',
  },
]
